import React, { useEffect, useMemo, useState } from "react";
import RoleToggle from "../../components/RoleToggle";
import ItemTab from "./_components/ItemTab";
import PromoTab from "./_components/PromoTab";
import ProductFetch from "./_components/ProductFetch";
import RTF, { RTFData } from "./_components/RTF";
import { useClientPermissions } from "../../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../features/auth/authSlice";
import { useLanguage } from "../../context/LanguageContext";

interface TabConfig {
  key: string;
  label: string;
  component: React.ReactNode;
  modules?: string[];
  operation?: string;
}

const hasPermission = (
  permissions: ReturnType<typeof selectClientPermissions>,
  modules: string[] | undefined,
  operation: string | undefined,
): boolean => {
  if (!permissions || !modules || !operation) return true; // no check = always show
  return modules.some((mod) =>
    permissions.some(
      (p) => p.module === mod && p.operations.includes(operation),
    ),
  );
};

const InverntoryProduct: React.FC = () => {
  const { t } = useLanguage();

  const data = useSelector((state: any) => state?.product?.data);

  const [activeRole1, setActiveRole1] = useState<string>(() => {
    return localStorage.getItem("InventoryOptActiveRole") || "Item";
  });

  const inventoryStats = {
    total: activeRole1 === "RTF" ? RTFData.length : (data?.total || 0),
    active:
      data?.productItems?.filter((p: any) => p?.status === "ACTIVE").length ||
      0,
    categories:
      new Set(data?.productItems?.map((p: any) => p?.category)).size || 0,
    totalCapacity:
      data?.productItems?.reduce((acc: number, item: any) => {
        let total = 0;
        try {
          let storageArray: any[] = [];
          if (Array.isArray(item.storage)) {
            storageArray = item.storage.map((s: any) =>
              typeof s === "string" ? JSON.parse(s) : s,
            );
          } else if (typeof item.storage === "string") {
            storageArray = [JSON.parse(item.storage)];
          } else {
            storageArray = [item.storage];
          }
          total = storageArray.reduce(
            (sum: number, s: any) => sum + Number(s.capacity || 0),
            0,
          );
        } catch {
          total = 0;
        }
        return acc + total;
      }, 0) || 0,
  };

  const { isLoading, error } = useClientPermissions();
  const permissions = useSelector(selectClientPermissions);

  useEffect(() => {
    localStorage.setItem("InventoryOptActiveRole", activeRole1);
  }, [activeRole1]);

  const tabs: TabConfig[] = useMemo(
    () => [
      {
        key: "Product",
        label: "Product",
        component: <ItemTab />,
        modules: ["products"], // allow both
        operation: "read",
      },
      /*
      {
        key: "Promo",
        label: "Promo",
        component: <PromoTab />,
        modules: ["promo"],
        operation: "read",
      },
      */
      {
        key: "RTF",
        label: "RTF",
        component: <RTF setActiveTab={setActiveRole1} />,
        modules: ["rtf"],
        operation: "read",
      },
    ],
    [],
  );

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) =>
      hasPermission(permissions, tab.modules, tab.operation),
    );
  }, [permissions, tabs]);

  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.some((t) => t.key === activeRole1)
    ) {
      setActiveRole1(visibleTabs[0].key);
    }
  }, [visibleTabs, activeRole1]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-64">
          <span className="text-sm text-gray-600">{t("Loading permissions...")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-64 text-red-600">
          <span className="text-sm">{t("Failed to load permissions")}</span>
        </div>
      </div>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center h-64 text-gray-500">
          <span className="text-sm">
            {t("You don't have access to any inventory features.")}
          </span>
        </div>
      </div>
    );
  }

  const activeTab = visibleTabs.find((t) => t.key === activeRole1);
  const activeComponent = activeTab?.component ?? visibleTabs[0].component;

  

  return (
    <div className="space-y-0">
      <div className="flex justify-between items-center pr-3">
        <RoleToggle
          options={visibleTabs.map((t) => t.label)}
          active={activeRole1}
          onChange={setActiveRole1}
          activeClassName="bg-[#000] text-[#FFFFFF] font-bold shadow-sm"
        />
        <div className="flex items-center gap-3 bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm text-xs select-none font-semibold h-8">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">{t("Total:")}</span>
            <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-md font-bold">
              {inventoryStats.total}
            </span>
          </div>
          {activeRole1 !== "RTF" && (
            <>
              <span className="h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Active:")}</span>
                <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-md font-bold">
                  {inventoryStats.active}
                </span>
              </div>
              <span className="h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Categories:")}</span>
                <span className="text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded-md font-bold">
                  {inventoryStats.categories}
                </span>
              </div>
              <span className="h-3 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500">{t("Capacity:")}</span>
                <span className="text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-md font-bold">
                  {inventoryStats.totalCapacity}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {activeComponent}
    </div>
  );
};

export default InverntoryProduct;
